import memoize from 'lodash.memoize';
import { getAllCanvases, getAllExternalResources, getAllImages, getAllLayers, getAllRanges, getAllServices } from '../all';
import * as technical from './iiif/technical';
import { createSelector } from 'reselect';
import * as descriptive from './iiif/descriptive';
import * as linking from './iiif/linking';
import * as structural from './iiif/structural';

var range = memoize(function (selector) {
  /**************************************************
   * Technical properties
   *
   * - getId
   * - getType
   * - getViewingHint
   * - getViewingDirection
   **************************************************/
  var getId = createSelector(selector, technical.getId);

  var getType = createSelector(selector, technical.getType);

  var getViewingHint = createSelector(selector, technical.getWhitelistedViewingHint(['individuals', 'paged', 'continuous', 'top']));

  var getViewingDirection = createSelector(selector, technical.getViewingDirection);

  /**************************************************
   * Descriptive properties
   *
   * - getLabel
   * - getMetadata
   * - getDescription
   * - getThumbnail
   * - getAttribution
   * - getLicense
   * - getLogo
   **************************************************/
  var getLabel = createSelector(selector, descriptive.getLabel);

  var getDescription = createSelector(selector, descriptive.getDescription);

  var getMetadata = createSelector(selector, descriptive.getMetadata);

  var getAttribution = createSelector(selector, descriptive.getAttribution);

  var getLogo = createSelector(selector, descriptive.getLogo);

  var getLicense = createSelector(selector, descriptive.getLicense);

  var getThumbnailId = createSelector(selector, descriptive.getThumbnailId);
  var getThumbnail = createSelector(getThumbnailId, getAllImages, function (thumbnailId, allImages) {
    return allImages[thumbnailId] || thumbnailId;
  });

  /**************************************************
   * Linking properties
   *
   * - getSeeAlso
   * - getService
   * - getRelated
   * - getRendering
   * - getWithin
   **************************************************/
  var getSeeAlsoIds = createSelector(selector, linking.getSeeAlso);
  var getSeeAlso = createSelector(getSeeAlsoIds, getAllExternalResources, function (seeAlsoIds, allExternalResources) {
    return seeAlsoIds.map(function (seeAlsoId) {
      return allExternalResources[seeAlsoId] || {
        '@id': seeAlsoId,
        label: [{ '@value': 'unknown', '@language': '@none' }]
      };
    });
  });

  var getServiceIds = createSelector(selector, linking.getService);
  var getService = createSelector(getServiceIds, getAllServices, function (serviceIds, allServices) {
    return serviceIds.map(function (serviceId) {
      return allServices[serviceId];
    });
  });

  var getRelatedIds = createSelector(selector, linking.getRelated);
  var getRelated = createSelector(getRelatedIds, getAllExternalResources, function (relatedIds, allExternalResources) {
    return relatedIds.map(function (relatedId) {
      return allExternalResources[relatedId];
    });
  });

  var getRenderingIds = createSelector(selector, linking.getRendering);
  var getRendering = createSelector(getRenderingIds, getAllExternalResources, function (renderingIds, allExternalResources) {
    return renderingIds.map(function (renderingId) {
      return allExternalResources[renderingId];
    });
  });

  var getWithinIds = createSelector(selector, linking.getWithin);
  var getWithin = createSelector(getWithinIds, getAllLayers, getAllExternalResources, function (withinIds, allLayers, allExternalResources) {
    return withinIds.map(function (within) {
      return allLayers[within.id] || allExternalResources[within.id];
    }).filter(function (e) {
      return e;
    });
  });

  var getStartCanvasId = createSelector(selector, linking.getStartCanvas);
  var getStartCanvas = createSelector(getStartCanvasId, getAllCanvases, function (canvasId, allCanvases) {
    return allCanvases[canvasId];
  });

  /**************************************************
   * Structural properties
   *
   * - getMembers
   * - getCanvases
   * - getRanges
   **************************************************/
  var getCanvasIds = createSelector(selector, structural.getRangeCanvases);
  var getCanvases = createSelector(getCanvasIds, getAllCanvases, function (canvasIds, allCanvases) {
    return canvasIds.map(function (canvasId) {
      return allCanvases[canvasId] || {
        '@id': canvasId,
        label: [{ '@language': 'none', '@value': 'unknown' }]
      };
    });
  });

  var getMemberIds = createSelector(selector, structural.getRangeMembers);
  var getMembers = createSelector(getMemberIds, getAllCanvases, getAllRanges, function (memberIds, allCanvases, allRanges) {
    return memberIds.map(function (memberId) {
      return allCanvases[memberId] || allRanges[memberId] || {
        '@id': memberId,
        label: [{ '@language': 'none', '@value': 'unknown' }]
      };
    });
  });

  var getRangeIds = createSelector(selector, structural.getRangeRanges);
  var getRanges = createSelector(getRangeIds, getAllRanges, function (rangeIds, allRanges) {
    return rangeIds.map(function (rangeId) {
      return allRanges[rangeId];
    });
  });

  return {
    // technical.
    getId: getId,
    getType: getType,
    getViewingHint: getViewingHint,
    getViewingDirection: getViewingDirection,

    // descriptive.
    getLabel: getLabel,
    getMetadata: getMetadata,
    getDescription: getDescription,
    getThumbnail: getThumbnail,
    getThumbnailId: getThumbnailId,
    getAttribution: getAttribution,
    getLicense: getLicense,
    getLogo: getLogo,

    // linking.
    getSeeAlso: getSeeAlso,
    getSeeAlsoIds: getSeeAlsoIds,
    getService: getService,
    getServiceIds: getServiceIds,
    getRelated: getRelated,
    getRelatedIds: getRelatedIds,
    getRendering: getRendering,
    getRenderingIds: getRenderingIds,
    getWithin: getWithin,
    getWithinIds: getWithinIds,
    getStartCanvas: getStartCanvas,
    getStartCanvasId: getStartCanvasId,

    // structural.
    getMembers: getMembers,
    getMemberIds: getMemberIds,
    getCanvases: getCanvases,
    getCanvasIds: getCanvasIds,
    getRanges: getRanges,
    getRangeIds: getRangeIds
  };
});

export default range;